import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit'
import {
  fetchPatientFolder,
  fetchFolderById,
  createNewFolder,
  updateFolder,
  deleteFolder,
  renameFolderAction,
  moveFolderAction,
  copyFolderAction,
  moveStudyToFolderAction,
  uploadFileAction,
  uploadFilesAction,
  deleteFileAction,
  toggleFileFavoriteAction,
  renameFileAction,
  moveFileAction,
  bulkMoveFilesAction,
  copyFileAction,
  moveFilesAndFoldersAction,
  attachFileToClaimAction,
  attachFileToClaimItemAction,
  fetchTotalStorage,
  fetchPatientStorage,
  fetchWorkspace,
  fetchFileDetails,
  fetchFolderDetails,
  fetchFilePreview,
  fetchFileDownload,
  fetchFolderZipEstimate,
  triggerFolderZipDownload,
} from '@store/actions/folder.action'
import { IFolder, IFile, IFolderState } from '@dataTypes/folder.types'

const initialState: IFolderState = {
  selectedFolder: null,
  childFolders: [],
  files: [],
  loading: false,
  error: null,
  successMessage: null,
  totalStorage: 0,
  patientStorage: 0,
  workspace: null,
  workspaceRequestId: null,
  fileDetails: null,
  folderDetails: null,
  previewPayload: null,
  downloadPayload: null,
  zipEstimate: null,
  zipDownloadLoading: false,
  filesWorkspaceRefreshKey: 0,
}

const folderSlice = createSlice({
  name: 'folder',
  initialState,
  reducers: {
    clearFolderError: (state) => {
      state.error = null
    },
    clearFolderSuccess: (state) => {
      state.successMessage = null
    },
    clearSelectedFolder: (state) => {
      state.selectedFolder = null
      state.childFolders = []
      state.files = []
    },
    updateChildFolders: (state, action: PayloadAction<IFolder[]>) => {
      state.childFolders = action.payload || []
    },
    updateFiles: (state, action: PayloadAction<IFile[]>) => {
      state.files = action.payload || []
    },
    addChildFolder: (state, action: PayloadAction<IFolder>) => {
      state.childFolders.push(action.payload)
    },
    removeChildFolder: (state, action: PayloadAction<string>) => {
      state.childFolders = state.childFolders.filter(
        (folder) => folder.folder_id !== action.payload,
      )
    },
    addFile: (state, action: PayloadAction<IFile>) => {
      state.files.push(action.payload)
    },
    removeFile: (state, action: PayloadAction<string>) => {
      state.files = state.files.filter(
        (file) => file.file_id !== action.payload,
      )
    },
    updateFile: (state, action: PayloadAction<IFile>) => {
      const index = state.files.findIndex(
        (file) => file.file_id === action.payload.file_id,
      )
      if (index !== -1) {
        state.files[index] = action.payload
      }
    },
    updateChildFolder: (state, action: PayloadAction<IFolder>) => {
      const index = state.childFolders.findIndex(
        (folder) => folder.folder_id === action.payload.folder_id,
      )
      if (index !== -1) {
        state.childFolders[index] = action.payload
      }
    },
  },
  extraReducers: (builder) => {
    // Fetch patient folder (single folder for the patient)
    builder
      .addCase(fetchPatientFolder.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        fetchPatientFolder.fulfilled,
        (state, action: PayloadAction<IFolder>) => {
          state.selectedFolder = action.payload
          state.childFolders = action.payload.child_folders || []
          state.files = action.payload.files || []
          state.loading = false
          state.error = null
          state.successMessage = 'Patient folder loaded successfully'
        },
      )
      .addCase(fetchPatientFolder.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

    // Fetch folder by ID
    builder
      .addCase(fetchFolderById.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        fetchFolderById.fulfilled,
        (state, action: PayloadAction<IFolder>) => {
          state.selectedFolder = action.payload
          state.childFolders = action.payload.child_folders || []
          state.files = action.payload.files || []
          state.loading = false
          state.successMessage = 'Folder loaded successfully'
        },
      )
      .addCase(fetchFolderById.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

    // Create folder
    builder
      .addCase(createNewFolder.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        createNewFolder.fulfilled,
        (state, action: PayloadAction<IFolder>) => {
          // Add to child folders if it's a child of the selected folder
          if (
            state.selectedFolder &&
            action.payload.parent_folder_id === state.selectedFolder.folder_id
          ) {
            state.childFolders.push(action.payload)
          }
          state.loading = false
          state.successMessage = 'Folder created successfully'
        },
      )
      .addCase(createNewFolder.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

    // Update folder
    builder
      .addCase(updateFolder.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        updateFolder.fulfilled,
        (state, action: PayloadAction<IFolder>) => {
          // Update selected folder if it's the same
          if (
            state.selectedFolder &&
            state.selectedFolder.folder_id === action.payload.folder_id
          ) {
            state.selectedFolder = action.payload
          }

          // Update child folder if it's in the list
          const childIndex = state.childFolders.findIndex(
            (folder) => folder.folder_id === action.payload.folder_id,
          )
          if (childIndex !== -1) {
            state.childFolders[childIndex] = action.payload
          }

          state.loading = false
          state.successMessage = 'Folder updated successfully'
        },
      )
      .addCase(updateFolder.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

    // Delete folder
    builder
      .addCase(deleteFolder.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        deleteFolder.fulfilled,
        (state, action: PayloadAction<string>) => {
          // Remove from child folders
          state.childFolders = state.childFolders.filter(
            (folder) => folder.folder_id !== action.payload,
          )

          // Clear selected folder if it was deleted
          if (
            state.selectedFolder &&
            state.selectedFolder.folder_id === action.payload
          ) {
            state.selectedFolder = null
            state.childFolders = []
            state.files = []
          }

          state.loading = false
          state.successMessage = 'Folder deleted successfully'
        },
      )
      .addCase(deleteFolder.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

    // Rename folder
    builder
      .addCase(renameFolderAction.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        renameFolderAction.fulfilled,
        (state, action: PayloadAction<IFolder>) => {
          // Update selected folder if it's the same
          if (
            state.selectedFolder &&
            state.selectedFolder.folder_id === action.payload.folder_id
          ) {
            state.selectedFolder = action.payload
          }

          // Update child folder if it's in the list
          const childIndex = state.childFolders.findIndex(
            (folder) => folder.folder_id === action.payload.folder_id,
          )
          if (childIndex !== -1) {
            state.childFolders[childIndex] = action.payload
          }

          state.loading = false
          state.successMessage = 'Folder renamed successfully'
        },
      )
      .addCase(renameFolderAction.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

    // Move study to folder
    builder
      .addCase(moveStudyToFolderAction.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(moveStudyToFolderAction.fulfilled, (state) => {
        state.loading = false
        state.successMessage = 'Study moved to folder successfully'
      })
      .addCase(moveStudyToFolderAction.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

    // Upload file
    builder
      .addCase(uploadFileAction.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        uploadFileAction.fulfilled,
        (state, action: PayloadAction<IFile>) => {
          // Add the uploaded file to the files array
          state.files.push(action.payload)
          state.loading = false
          state.successMessage = 'File uploaded successfully'
        },
      )
      .addCase(uploadFileAction.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

    // Delete file
    builder
      .addCase(deleteFileAction.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        deleteFileAction.fulfilled,
        (state, action: PayloadAction<string>) => {
          // Remove the deleted file from the files array
          state.files = state.files.filter(
            (file) => file.file_id !== action.payload,
          )
          state.loading = false
          state.successMessage = 'File deleted successfully'
        },
      )
      .addCase(deleteFileAction.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

    // Toggle file favorite
    builder
      .addCase(toggleFileFavoriteAction.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        toggleFileFavoriteAction.fulfilled,
        (state, action: PayloadAction<IFile>) => {
          // Update the file in the files array
          const index = state.files.findIndex(
            (file) => file.file_id === action.payload.file_id,
          )
          if (index !== -1) {
            state.files[index] = action.payload
          }
          state.loading = false
          state.successMessage = `File ${action.payload.is_favorite ? 'added to' : 'removed from'} favorites`
        },
      )
      .addCase(toggleFileFavoriteAction.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

    // Fetch total storage
    builder
      .addCase(fetchTotalStorage.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        fetchTotalStorage.fulfilled,
        (state, action: PayloadAction<number>) => {
          state.totalStorage = action.payload
          state.loading = false
        },
      )
      .addCase(fetchTotalStorage.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

    // Fetch patient storage
    builder
      .addCase(fetchPatientStorage.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        fetchPatientStorage.fulfilled,
        (state, action: PayloadAction<number>) => {
          state.patientStorage = action.payload
          state.loading = false
        },
      )
      .addCase(fetchPatientStorage.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

    // Fetch workspace
    builder
      .addCase(fetchWorkspace.pending, (state, action) => {
        state.loading = true
        state.error = null
        // Track the latest in-flight request so out-of-order responses
        // (e.g. a slower root fetch resolving after a folder fetch) can be
        // discarded instead of clobbering the current folder's contents.
        state.workspaceRequestId = action.meta.requestId
      })
      .addCase(fetchWorkspace.fulfilled, (state, action) => {
        // Ignore stale responses that are no longer the latest request.
        if (
          state.workspaceRequestId !== null &&
          action.meta.requestId !== state.workspaceRequestId
        ) {
          return
        }
        state.workspace = action.payload
        const rootFolderId = action.payload.scope.root_folder_id
        state.childFolders = action.payload.folders.map((folder) => ({
          folder_id: folder.folder_id,
          folder_name: folder.folder_name,
          patient_id: action.payload.scope.patient_id,
          parent_folder_id:
            rootFolderId && folder.parent_folder_id === rootFolderId
              ? null
              : folder.parent_folder_id,
          path: folder.path,
          folder_size: folder.folder_size,
          total_items: folder.total_items,
          total_files: folder.total_files,
          total_folders: folder.total_folders,
          created_at: new Date(),
          updated_at: folder.date_time_updated_at
            ? new Date(folder.date_time_updated_at)
            : new Date(),
          child_folders: [],
          files: [],
        }))
        state.files = action.payload.files.map((file) => ({
          file_id: file.file_id,
          patient_id: action.payload.scope.patient_id,
          folder_id: file.folder_id,
          file_name: file.file_name,
          original_name: file.original_name,
          file_path: '',
          file_type: file.file_type,
          mime_type: file.mime_type,
          file_size: file.file_size,
          file_extension: '',
          file_category: file.ui_type,
          is_dicom: file.ui_type === 'X-Ray',
          dicom_metadata: null,
          study_uid: file.study_uid,
          series_uid: file.series_uid,
          sop_uid: file.sop_uid,
          thumbnail_path: null,
          preview_path: null,
          tags: null,
          description: null,
          is_favorite: file.is_favorite,
          download_count: file.download_count,
          last_accessed: file.last_accessed
            ? new Date(file.last_accessed)
            : null,
          expires_at: null,
          date_time_created_at: file.date_time_created_at
            ? new Date(file.date_time_created_at)
            : new Date(),
          date_time_updated_at: new Date(),
        }))
        state.loading = false
      })
      .addCase(fetchWorkspace.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

    // Details endpoints
    builder
      .addCase(fetchFileDetails.pending, (state) => {
        state.error = null
      })
      .addCase(fetchFileDetails.fulfilled, (state, action) => {
        state.fileDetails = action.payload
      })
      .addCase(fetchFileDetails.rejected, (state, action) => {
        state.error = action.payload as string
      })

    builder
      .addCase(fetchFolderDetails.pending, (state) => {
        state.error = null
      })
      .addCase(fetchFolderDetails.fulfilled, (state, action) => {
        state.folderDetails = action.payload
      })
      .addCase(fetchFolderDetails.rejected, (state, action) => {
        state.error = action.payload as string
      })

    // Preview / download payloads
    builder
      .addCase(fetchFilePreview.fulfilled, (state, action) => {
        state.previewPayload = action.payload
      })
      .addCase(fetchFilePreview.rejected, (state, action) => {
        state.error = action.payload as string
      })

    builder
      .addCase(fetchFileDownload.fulfilled, (state, action) => {
        state.downloadPayload = action.payload
      })
      .addCase(fetchFileDownload.rejected, (state, action) => {
        state.error = action.payload as string
      })

    // Folder zip
    builder
      .addCase(fetchFolderZipEstimate.pending, (state) => {
        state.error = null
      })
      .addCase(fetchFolderZipEstimate.fulfilled, (state, action) => {
        state.zipEstimate = action.payload
      })
      .addCase(fetchFolderZipEstimate.rejected, (state, action) => {
        state.error = action.payload as string
      })

    builder
      .addCase(triggerFolderZipDownload.pending, (state) => {
        state.zipDownloadLoading = true
        state.error = null
      })
      .addCase(triggerFolderZipDownload.fulfilled, (state) => {
        state.zipDownloadLoading = false
      })
      .addCase(triggerFolderZipDownload.rejected, (state, action) => {
        state.zipDownloadLoading = false
        state.error = action.payload as string
      })

    builder.addMatcher(
      isAnyOf(
        createNewFolder.fulfilled,
        updateFolder.fulfilled,
        deleteFolder.fulfilled,
        renameFolderAction.fulfilled,
        moveFolderAction.fulfilled,
        copyFolderAction.fulfilled,
        moveStudyToFolderAction.fulfilled,
        uploadFileAction.fulfilled,
        uploadFilesAction.fulfilled,
        deleteFileAction.fulfilled,
        toggleFileFavoriteAction.fulfilled,
        renameFileAction.fulfilled,
        moveFileAction.fulfilled,
        bulkMoveFilesAction.fulfilled,
        copyFileAction.fulfilled,
        moveFilesAndFoldersAction.fulfilled,
        attachFileToClaimAction.fulfilled,
        attachFileToClaimItemAction.fulfilled,
      ),
      (state) => {
        state.filesWorkspaceRefreshKey += 1
      },
    )
  },
})

export const {
  clearFolderError,
  clearFolderSuccess,
  clearSelectedFolder,
  updateChildFolders,
  updateFiles,
  addChildFolder,
  removeChildFolder,
  addFile,
  removeFile,
  updateFile,
  updateChildFolder,
} = folderSlice.actions

export default folderSlice.reducer
